import React, { useCallback, useEffect, useState } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/utils';

interface InstagramCarouselProps {
  images: string[];
  className?: string;
  showControls?: boolean;
  showDots?: boolean;
  onImageChange?: (index: number) => void;
}

export const InstagramCarousel: React.FC<InstagramCarouselProps> = ({
  images,
  className,
  showControls = true,
  showDots = true,
  onImageChange,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    skipSnaps: false,
    dragFree: false,
    watchDrag: !isZoomed, // Disable drag when zoomed
  });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback((index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const index = emblaApi.selectedScrollSnap();
    setCurrentIndex(index);
    onImageChange?.(index);
  }, [emblaApi, onImageChange]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  // Re-initialize embla when zoom state changes
  useEffect(() => {
    if (emblaApi) {
      emblaApi.reInit({ watchDrag: !isZoomed });
    }
  }, [emblaApi, isZoomed]);

  if (!images || images.length === 0) {
    return null;
  }

  if (images.length === 1) {
    return (
      <div className={cn("relative w-full", className)}>
        <div className="bg-muted overflow-hidden max-h-[350px] md:max-h-[400px] lg:max-h-[500px] flex items-center">
          <TransformWrapper
            initialScale={1}
            minScale={1}
            maxScale={3}
            centerOnInit={true}
            wheel={{ step: 0.1 }}
            pinch={{ step: 0.1 }}
            doubleClick={{ disabled: false, step: 0.7 }}
            panning={{
              velocityDisabled: true,
              disabled: false, // Allow panning for single images
              lockAxisX: false,
              lockAxisY: false,
            }}
          >
            <TransformComponent
              wrapperClass="w-full h-full flex items-center justify-center"
              contentClass="w-full h-full flex items-center justify-center"
            >
              <img
                src={images[0]}
                alt="Image"
                className="w-full h-full object-cover"
                loading="lazy"
                draggable={false}
              />
            </TransformComponent>
          </TransformWrapper>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("relative w-full", className)}>
      <div className="bg-muted overflow-hidden max-h-[350px] md:max-h-[400px] lg:max-h-[500px]">
        <div className="embla" ref={emblaRef}>
          <div className="embla__container flex">
            {images.map((image, index) => (
              <div key={index} className="embla__slide flex-[0_0_100%] min-w-0">
                <div className="w-full h-[350px] md:h-[400px] lg:h-[500px] flex items-center justify-center">
                  <TransformWrapper
                    initialScale={1}
                    minScale={1}
                    maxScale={3}
                    centerOnInit={true}
                    wheel={{ step: 0.1 }}
                    pinch={{ step: 0.1 }}
                    doubleClick={{ disabled: false, step: 0.7 }}
                    panning={{
                      velocityDisabled: true,
                      disabled: !isZoomed, // Only allow panning when zoomed
                      lockAxisX: !isZoomed,
                      lockAxisY: !isZoomed,
                    }}
                    onTransformed={(ref) => {
                      const newIsZoomed = ref.state.scale > 1;
                      if (newIsZoomed !== isZoomed) {
                        setIsZoomed(newIsZoomed);
                      }
                    }}
                  >
                    <TransformComponent
                      wrapperClass="w-full h-full flex items-center justify-center"
                      contentClass="w-full h-full flex items-center justify-center"
                    >
                      <img
                        src={image}
                        alt={`Image ${index + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        draggable={false}
                      />
                    </TransformComponent>
                  </TransformWrapper>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation Controls */}
      {showControls && images.length > 1 && (
        <>
          <Button
            variant="outline"
            size="icon"
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-background/80 backdrop-blur-sm border-border/50 hover:bg-background/90 z-10"
            onClick={scrollPrev}
            disabled={currentIndex === 0}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-background/80 backdrop-blur-sm border-border/50 hover:bg-background/90 z-10"
            onClick={scrollNext}
            disabled={currentIndex === images.length - 1}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </>
      )}

      {/* Dots Indicator */}
      {showDots && images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
          {images.map((_, index) => (
            <button
              key={index}
              className={cn(
                "w-2 h-2 rounded-full transition-all duration-200",
                index === currentIndex
                  ? "bg-primary scale-110"
                  : "bg-background/60 hover:bg-background/80"
              )}
              onClick={() => scrollTo(index)}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Image Counter */}
      {images.length > 1 && (
        <div className="absolute top-4 right-4 bg-background/80 backdrop-blur-sm text-foreground px-2 py-1 rounded-md text-sm z-10">
          {currentIndex + 1} / {images.length}
        </div>
      )}
    </div>
  );
};